import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRequest, deleteRequest } from '../../apis/ApiInstence';
import axios from 'axios';
import { toast } from 'react-toastify';
import AdminTable from 'common/AdminTable';

const getMediaUrl = (filename, folder = 'news') => {
  if (!filename) return '';
  const clean = String(filename).trim();
  if (!clean) return '';
  if (clean.startsWith('http://') || clean.startsWith('https://')) {
    return clean;
  }
  const base = (
    process.env.REACT_APP_BACKEND ||
    (process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL.replace('/api/v1/', '').replace('/api/v1', '') : '') ||
    'https://sfsdifhapi.bundelichugli.com'
  ).replace(/\/+$/, '');

  return encodeURI(`${base}/uploads/${folder}/${clean}`);
};

const ThumbnailCell = ({ thumbnail }) => {
  const [imgError, setImgError] = useState(false);
  const url = getMediaUrl(thumbnail, 'news');

  if (!url || imgError) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-muted bg-light border rounded mx-auto"
        style={{ width: '80px', height: '52px', fontSize: '10px' }}
        title="No image"
      >
        <i className="fa-solid fa-image text-secondary mb-1"></i>
        <span>No img</span>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="Thumbnail"
      style={{
        width: '80px',
        height: '52px',
        objectFit: 'cover',
        borderRadius: '6px',
        display: 'block',
        margin: '0 auto',
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}
      onError={() => setImgError(true)}
    />
  );
};

const ReporterImageCell = ({ reporterImage }) => {
  const [imgError, setImgError] = useState(false);
  const url = getMediaUrl(reporterImage, 'news');

  if (!url || imgError) {
    return <span className="text-muted">-</span>;
  }

  return (
    <img
      src={url}
      alt="Reporter"
      style={{
        width: '40px',
        height: '40px',
        objectFit: 'cover',
        borderRadius: '50%',
        display: 'block',
        margin: '0 auto',
        border: '1.5px solid #003366'
      }}
      onError={() => setImgError(true)}
    />
  );
};

const ListNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState({
    open: false,
    title: '',
    description: ''
  });

  const [formData, setFormData] = useState({
    news_title: '',
    description: '',
    category: '',
    state: '',
    language: '',
    youtube_url: '',
    reporter_name: '',
    thumbnail: null,
    reporter_image: null,
    existingImage: '',
    existingReporterImage: '',
    is_active: 1
  });

  const fetchNews = async () => {
    try {
      setLoading(true);
      const res = await getRequest('product');

      if (Array.isArray(res?.data)) {
        setNewsList(res.data);
      } else {
        setNewsList([]);
        toast.error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Fetch failed:', error);
      toast.error('❌ Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleRemove = async (id) => {
    if (!window.confirm('Delete this news?')) return;

    try {
      await deleteRequest(`product/${id}`);
      toast.success('🗑️ News deleted!');
      fetchNews();
    } catch (err) {
      console.error(err);
      toast.error('❌ Failed to delete news');
    }
  };

  const handleEditClick = (news) => {
    setSelectedNews(news);

    setFormData({
      news_title: news.news_title || '',
      description: news.description || '',
      category: news.category || '',
      state: news.state || '',
      language: news.language || '',
      youtube_url: news.youtube_url || '',
      reporter_name: news.reporter_name || '',
      thumbnail: null,
      reporter_image: null,
      existingImage: news.thumbnail ? getMediaUrl(news.thumbnail, 'news') : '',
      existingReporterImage: news.reporter_image ? getMediaUrl(news.reporter_image, 'news') : '',
      is_active: news.is_active ?? 1
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };
  const openDescriptionModal = (title, description) => {
    setDescriptionModal({
      open: true,
      title: title || 'News Description',
      description: description || '-'
    });
  };
  const closeDescriptionModal = () => {
    setDescriptionModal({
      open: false,
      title: '',
      description: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, type, value, files, checked } = e.target;

    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveChanges = async () => {
    const updatedData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'thumbnail' || key === 'reporter_image') {
        if (value) updatedData.append(key, value);
      } else if (!key.startsWith('existing')) {
        updatedData.append(key, value);
      }
    });

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

      await axios.patch(
        `${backendUrl}/product/${selectedNews.id}`,
        updatedData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('News updated successfully');
      fetchNews();
      closeModal();
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Failed to update news';
      toast.error(typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg));
    }
  };

  const tableHeaders = [
    {
      Header: 'ID',
      accessor: 'id',
      Cell: ({ row }) => (
        <span style={{ fontWeight: '700', color: '#003366' }}>
          #{row.original.id}
        </span>
      )
    },
    {
      Header: 'Image',
      accessor: 'thumbnail',
      Cell: ({ row }) => (
        <ThumbnailCell thumbnail={row.original.thumbnail} />
      )
    },
    {
      Header: 'Title',
      accessor: 'news_title',
      Cell: ({ row }) => (
        <div
          style={{
            maxWidth: '240px',
            minWidth: '160px',
            textAlign: 'left',
            fontWeight: '600',
            fontSize: '13px',
            lineHeight: '1.45',
            color: '#212529'
          }}
        >
          {row.original.news_title || '-'}
        </div>
      )
    },
    {
      Header: 'Reporter',
      accessor: 'reporter_name',
      Cell: ({ row }) => (
        <span style={{ fontWeight: '500', fontSize: '13px' }}>
          {row.original.reporter_name || '-'}
        </span>
      )
    },
    {
      Header: 'Reporter Image',
      accessor: 'reporter_image',
      Cell: ({ row }) => (
        <ReporterImageCell
          reporterImage={row.original.reporter_image}
          reporterName={row.original.reporter_name}
        />
      )
    },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => {
        const fullText = row.original.description || '-';
        const maxLength = 60;
        const isLong = fullText.length > maxLength;
        const shortText = isLong ? `${fullText.slice(0, maxLength)}...` : fullText;

        return (
          <div
            style={{
              maxWidth: '220px',
              minWidth: '140px',
              textAlign: 'left',
              fontSize: '12px',
              color: '#555',
              lineHeight: '1.4'
            }}
          >
            <span>{shortText}</span>
            {isLong && (
              <button
                className="btn btn-link p-0 ml-1 font-weight-bold"
                style={{ fontSize: '11px', textDecoration: 'none', color: '#003366' }}
                onClick={() => openDescriptionModal(row.original.news_title, fullText)}
              >
                Show more
              </button>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Category',
      accessor: 'category',
      Cell: ({ row }) => (
        <span
          className="badge px-2 py-1 text-white text-capitalize"
          style={{ backgroundColor: '#003366', fontSize: '11px', fontWeight: '500' }}
        >
          {row.original.category || '-'}
        </span>
      )
    },
    {
      Header: 'State',
      accessor: 'state',
      Cell: ({ row }) => <span>{row.original.state || '-'}</span>
    },
    {
      Header: 'Language',
      accessor: 'language',
      Cell: ({ row }) => <span className="text-capitalize">{row.original.language || '-'}</span>
    },
    {
      Header: 'YouTube',
      accessor: 'youtube_url',
      Cell: ({ row }) =>
        row.original.youtube_url ? (
          <a
            href={row.original.youtube_url}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-outline-danger px-2 py-0"
            style={{ fontSize: '11px', borderRadius: '12px', whiteSpace: 'nowrap' }}
          >
            <i className="fa-brands fa-youtube mr-1"></i> View
          </a>
        ) : (
          '-'
        )
    },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ row }) => (
        <span
          className={`badge px-2 py-1 ${row.original.is_active ? 'badge-success' : 'badge-secondary'}`}
          style={{ fontSize: '11px' }}
        >
          {row.original.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center align-items-center" style={{ gap: '6px' }}>
          <button
            className="btn btn-sm btn-primary px-2 py-1"
            style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
            onClick={() => handleEditClick(row.original)}
            title="Edit News"
          >
            <i className="fa-solid fa-pen-to-square mr-1"></i> Edit
          </button>
          <button
            className="btn btn-sm btn-danger px-2 py-1"
            style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
            onClick={() => handleRemove(row.original.id)}
            title="Remove News"
          >
            <i className="fa-solid fa-trash mr-1"></i> Remove
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-4 mt-md-5 ournews-page">
        <div
          className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center p-3 rounded mx-2 mx-md-4 mb-3 shadow-sm"
          style={{ backgroundColor: '#003366', color: '#fff' }}
        >
          <h4 className="m-0 font-weight-bold text-white">
            <i className="fa-solid fa-newspaper mr-2"></i>News List
          </h4>
          <Link
            to="/ournews/add"
            className="btn btn-sm btn-light font-weight-bold mt-2 mt-sm-0"
            style={{ color: '#003366' }}
          >
            <i className="fa-solid fa-plus mr-1"></i> Add News
          </Link>
        </div>

        {loading && <p className="text-white mx-2 mx-md-4">Loading...</p>}
        {!loading && newsList.length === 0 && <p className="text-white mx-2 mx-md-4">No news found.</p>}

        {!loading && newsList.length > 0 ? (
          <>
            <div className="d-none d-md-block">
              <AdminTable tableHeaders={tableHeaders} tableData={newsList} />
            </div>

            <div className="d-md-none px-2">
              {newsList.map((item) => (
                <div key={item.id} className="card mb-3 shadow-sm" style={{ borderRadius: '8px' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong style={{ color: '#003366' }}>#{item.id}</strong>
                      <span className={item.is_active ? 'badge badge-success' : 'badge badge-secondary'}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <h6 className="mb-2 font-weight-bold" style={{ lineHeight: '1.4' }}>
                      {item.news_title || '-'}
                    </h6>

                    {item.thumbnail ? (
                      <img
                        src={getMediaUrl(item.thumbnail, 'news')}
                        alt="Thumbnail"
                        className="img-fluid rounded mb-2 ournews-mobile-thumb"
                        style={{ maxHeight: '180px', objectFit: 'cover', width: '100%' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}

                    <div className="mb-2">
                      <strong>Reporter:</strong> {item.reporter_name || '-'}
                    </div>

                    {item.reporter_image ? (
                      <img
                        src={getMediaUrl(item.reporter_image, 'news')}
                        alt="Reporter"
                        width={44}
                        height={44}
                        className="rounded-circle mb-2"
                        style={{ objectFit: 'cover', border: '1.5px solid #003366' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}

                    <p className="mb-1" style={{ fontSize: '13px' }}>
                      <strong>Category:</strong>{' '}
                      <span className="badge badge-info text-white">{item.category || '-'}</span>
                    </p>
                    <p className="mb-1" style={{ fontSize: '13px' }}>
                      <strong>State:</strong> {item.state || '-'}
                    </p>
                    <p className="mb-1" style={{ fontSize: '13px' }}>
                      <strong>Language:</strong> <span className="text-capitalize">{item.language || '-'}</span>
                    </p>
                    {item.youtube_url ? (
                      <p className="mb-2" style={{ fontSize: '13px' }}>
                        <strong>YouTube:</strong>{' '}
                        <a
                          href={item.youtube_url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-outline-danger px-2 py-0"
                          style={{ fontSize: '11px', borderRadius: '12px' }}
                        >
                          <i className="fa-brands fa-youtube mr-1"></i> View
                        </a>
                      </p>
                    ) : null}

                    <p className="mb-2 text-muted" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      <strong>Description:</strong>{' '}
                      {(item.description || '-').length > 80 ? `${item.description.slice(0, 80)}...` : item.description || '-'}
                      {(item.description || '').length > 80 ? (
                        <button
                          className="btn btn-link p-0 ml-1 font-weight-bold"
                          style={{ fontSize: '12px', textDecoration: 'none', color: '#003366' }}
                          onClick={() => openDescriptionModal(item.news_title, item.description)}
                        >
                          Show more
                        </button>
                      ) : null}
                    </p>

                    <div className="d-flex" style={{ gap: 8 }}>
                      <button className="btn btn-primary btn-sm flex-fill" onClick={() => handleEditClick(item)}>
                        <i className="fa-solid fa-pen-to-square mr-1"></i> Edit
                      </button>
                      <button className="btn btn-danger btn-sm flex-fill" onClick={() => handleRemove(item.id)}>
                        <i className="fa-solid fa-trash mr-1"></i> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      {showModal && selectedNews && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg ournews-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit News</h5>
                <button type="button" className="close" onClick={closeModal}>
                  <span>&times;</span>
                </button>
              </div>

              <div className="modal-body">
                <div className="row">
                  <div className="col-12 col-md-6 mb-2">
                    <input name="news_title" value={formData.news_title} onChange={handleFormChange} placeholder="Title" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input
                      name="reporter_name"
                      value={formData.reporter_name}
                      onChange={handleFormChange}
                      placeholder="Reporter Name"
                      className="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input name="category" value={formData.category} onChange={handleFormChange} placeholder="Category" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input name="state" value={formData.state} onChange={handleFormChange} placeholder="State" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input name="language" value={formData.language} onChange={handleFormChange} placeholder="Language" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input
                      name="youtube_url"
                      value={formData.youtube_url}
                      onChange={handleFormChange}
                      placeholder="YouTube URL"
                      className="form-control"
                    />
                  </div>
                  <div className="col-12 mb-2">
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Description"
                      className="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input type="file" name="thumbnail" onChange={handleFormChange} className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input type="file" name="reporter_image" onChange={handleFormChange} className="form-control" />
                  </div>
                </div>

                <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: 10 }}>
                  {formData.thumbnail ? (
                    <img src={URL.createObjectURL(formData.thumbnail)} width={120} alt="preview" className="img-fluid rounded" />
                  ) : formData.existingImage ? (
                    <img src={formData.existingImage} width={120} alt="existing" className="img-fluid rounded" />
                  ) : null}

                  {formData.reporter_image ? (
                    <img
                      src={URL.createObjectURL(formData.reporter_image)}
                      width={80}
                      height={80}
                      alt="preview"
                      style={{ borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : formData.existingReporterImage ? (
                    <img
                      src={formData.existingReporterImage}
                      width={80}
                      height={80}
                      alt="existing"
                      style={{ borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : null}
                </div>

                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active === 1}
                    onChange={handleFormChange}
                    className="form-check-input"
                  />
                  <label className="form-check-label" htmlFor="is_active">
                    Active
                  </label>
                </div>

              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveChanges}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {descriptionModal.open ? (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered ournews-desc-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5>{descriptionModal.title}</h5>
                <button type="button" className="close" onClick={closeDescriptionModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{descriptionModal.description}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDescriptionModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ListNews;
